import React, { useEffect, useState } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { Row, Button, Select, Typography, Avatar, Tooltip, Col } from 'antd';

import { WrapperSelect } from 'common/components';
import ModalUploadAdvancedFile from 'common/components/advanced-search/ModalUploadAdvancedFile';
import { AvatarPropertiesItem } from 'common/components/mapping-properties/MappingSourceProperties';

import classnames from 'classnames';
import RenderValue from './RenderValue';
import RenderValueIn from './RenderValueIn';
import RenderValueBetween from './RenderValueBetween';
import RenderBtnUploadFile from './RenderBtnUploadFile';
import { Draggable } from 'react-beautiful-dnd';
import SwitchConjunction from './SwitchConjunction';
import { findProductPropertyEnums } from 'pages/product-full-view/components/product-detail-view/utils';
import { RenderStrictMode } from '../hook/renderTree';
import { useGetProductEnums } from 'hooks';
import { useWindowSize } from 'hooks/windowSize';
import { OPERATOR } from 'common/components/grid-view/components/content-pane/advancedFilter/utils';

const QueryDraggable = (props) => {
  const {
    index,
    property,
    viewMode,
    draggableId,
    handleConjunction,
    queryConditionsId,
    handleQueryCondition,
    handleQueryDateBetween,
    handleRemoveQueryConditions,
    handleUpdateQueryConditionFile,
  } = props;

  const [visibleModal, setVisibleModal] = useState(false);
  const [possibleOperator, setPossibleOperator] = useState([]);
  const [acceptableValueSelect, setAcceptableValueSelect] = useState([]);
  const [queryConditionValue, setQueryConditionValue] = useState();

  const [screenWidth, screenHeight] = useWindowSize();

  const handleOpenModalUploadFile = () => {
    setVisibleModal(true);
  };
  const handleChangeOperator = (value) => {
    setQueryConditionValue(value);
    handleQueryCondition(property?.valuePath, value, 'operator');
  };

  const handleUpdateFile = (value, valuePath, key) => {
    setPossibleOperator(['In', 'Not In']);
    setAcceptableValueSelect(value);
    setQueryConditionValue('In');
    handleUpdateQueryConditionFile(
      valuePath,
      'In',
      'operator',
      value,
      key,
      value,
      'acceptableValueTemp'
    );
  };

  const { productEnums } = useGetProductEnums();

  const propertyEnums = findProductPropertyEnums(
    property?.listName,
    productEnums
  );

  const value =
    property?.operator ||
    (property?.possibleOperator?.length && property?.possibleOperator[0]);

  const handleRemoveUploadFile = () => {
    const defaultOperator = property?.possibleOperator[0];

    handleInitQuery();
    setQueryConditionValue(defaultOperator);
    setPossibleOperator(property?.possibleOperator ?? []);
    handleUpdateQueryConditionFile(
      property?.valuePath,
      defaultOperator,
      'operator',
      undefined,
      'values',
      undefined,
      'acceptableValueTemp'
    );
  };

  const handleInitQuery = () => {
    const acceptableValue = [...(property?.acceptableValue ?? [])];
    setQueryConditionValue(value);
    setAcceptableValueSelect(acceptableValue);
    if (property?.acceptableValueTemp?.length > 0) {
      setPossibleOperator(['In', 'Not In']);
    } else {
      setPossibleOperator(property?.possibleOperator ?? []);
    }
  };

  const isBool =
    property?.dataType === 'boolean' || property?.dataType === 'bool';

  useEffect(() => {
    const initArgument =
      isBool && property?.value == null
        ? [property?.valuePath, value, 'operator', false, 'value']
        : [property?.valuePath, value, 'operator'];
    handleInitQuery();
    handleUpdateQueryConditionFile(...initArgument);
  }, [property]);

  const mappingOperator =
    possibleOperator?.map((item) => {
      return {
        value: item,
        displayName: OPERATOR[item] || item,
      };
    }) || [];

  return (
    <Draggable draggableId={draggableId} index={index} isDragDisabled>
      {(provided, snapshot) => {
        const backgroundColor = () => {
          if (snapshot.combineTargetFor) {
            return '#69c0ff';
          } else {
            return;
          }
        };

        return (
          <>
            {
              <div className='query-condition-draggable'>
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  className={classnames({
                    'query-condition-draggable__card': true,
                    'query-condition-draggable__card-query': true,
                  })}
                  style={{
                    backgroundColor: backgroundColor(),
                    pointerEvents: viewMode ? 'none' : 'auto',
                    cursor: 'default',
                  }}
                >
                  {property?.isParent ? (
                    <div style={{ display: 'flex' }}>
                      <SwitchConjunction
                        viewMode={viewMode}
                        property={property}
                        handleConjunction={handleConjunction}
                        title={property.parentDisplayName}
                      />
                      <RenderStrictMode
                        style={{ marginLeft: 'auto' }}
                        handleQueryCondition={handleQueryCondition}
                        queryConditions={property}
                      />
                    </div>
                  ) : (
                    <Row style={{ position: 'relative' }}>
                      <Col span={12} style={{ display: 'flex' }}>
                        {
                          <Avatar
                            size='small'
                            className='entities-draggable__data-type'
                            shape='square'
                            style={{ margin: '3px 0' }}
                            icon={
                              <AvatarPropertiesItem
                                propertyType={property?.dataType}
                              />
                            }
                          />
                        }
                        <Typography.Text
                          className='query-condition-draggable__label'
                          ellipsis
                        >
                          <span>
                            {property?.fullPathDisplayName ??
                              property?.displayName}
                          </span>
                        </Typography.Text>
                      </Col>
                      {
                        <Col
                          span={12}
                          style={{
                            display: 'flex',
                            justifyContent: 'end',
                            alignItems:
                              property?.acceptableValueTemp?.length > 100000
                                ? 'flex-start'
                                : 'center',
                          }}
                        >
                          <WrapperSelect
                            className='query-condition-draggable__select'
                            size='small'
                            getPopupContainer={() =>
                              document.getElementById(queryConditionsId)
                            }
                            placeholder='Please select value'
                            optionFilterProp='children'
                            listHeight={screenHeight <= 768 ? 70 : 150}
                            value={queryConditionValue}
                            onChange={(value) => {
                              handleChangeOperator(value);
                            }}
                            filterOption={(input, option) =>
                              option.children
                                ?.toLowerCase()
                                .indexOf(input?.toLowerCase()) >= 0
                            }
                            allowClear={false}
                          >
                            {mappingOperator.map((item, index) => {
                              return (
                                <Select.Option key={index} value={item?.value}>
                                  <Tooltip
                                    title={item?.value}
                                    arrowPointAtCenter
                                  >
                                    {item?.displayName}
                                  </Tooltip>
                                </Select.Option>
                              );
                            })}
                          </WrapperSelect>
                          {queryConditionValue === 'Between' && (
                            <RenderValueBetween
                              property={property}
                              propertyEnums={propertyEnums}
                              acceptableValueSelect={acceptableValueSelect}
                              queryConditionsId={queryConditionsId}
                              handleQueryCondition={handleQueryCondition}
                              handleQueryDateBetween={handleQueryDateBetween}
                            />
                          )}
                          {(queryConditionValue === 'In' ||
                            queryConditionValue === 'Not In') && (
                            <RenderValueIn
                              property={property}
                              propertyEnums={propertyEnums}
                              acceptableValueSelect={acceptableValueSelect}
                              handleQueryCondition={handleQueryCondition}
                              queryConditionsId={queryConditionsId}
                            />
                          )}
                          {queryConditionValue !== 'Between' &&
                            queryConditionValue !== 'Is Not Null' &&
                            queryConditionValue !== 'Is Null' &&
                            queryConditionValue !== 'In' &&
                            queryConditionValue !== 'Not In' && (
                              <RenderValue
                                property={property}
                                propertyEnums={propertyEnums}
                                acceptableValueSelect={acceptableValueSelect}
                                handleQueryCondition={handleQueryCondition}
                                queryConditionsId={queryConditionsId}
                              />
                            )}
                          {(queryConditionValue === 'Is Not Null' ||
                            queryConditionValue === 'Is Null') && (
                            <div className='query-condition-draggable__input'></div>
                          )}
                          {
                            <>
                              <RenderBtnUploadFile
                                property={property}
                                possibleOperator={possibleOperator}
                                handleUploadAdvancedFile={
                                  handleRemoveUploadFile
                                }
                                handleOpenModalUploadFile={
                                  handleOpenModalUploadFile
                                }
                              />
                              <Tooltip
                                className='query-condition-draggable__remove'
                                title='Remove query'
                              >
                                <Button
                                  danger
                                  type='text'
                                  icon={<CloseOutlined />}
                                  onClick={() => {
                                    handleRemoveQueryConditions(
                                      property?.valuePath
                                    );
                                  }}
                                />
                              </Tooltip>
                              <div className='query-condition-draggable__replace-remove'></div>
                            </>
                          }
                        </Col>
                      }
                    </Row>
                  )}

                  <ModalUploadAdvancedFile
                    visibleModal={visibleModal}
                    setVisibleModal={setVisibleModal}
                    handleUploadAdvancedFile={(data) =>
                      handleUpdateFile(data, property?.valuePath, 'values')
                    }
                  />
                </div>
              </div>
            }
          </>
        );
      }}
    </Draggable>
  );
};

export default React.memo(QueryDraggable);
